import styled from 'styled-components';
import { StarGray, StarYellow } from '../../icon/icon';
import { useState } from 'react';
import {
  fetchPreferenceCompanyCancel,
  fetchPreferenceCompanyRegistReation,
} from '../../../api/preference';
import { useHistory } from 'react-router-dom';
import { AlertModal } from '../../modal/alert.modal';

interface PreferenceCompanyResponse {
  id: number;
  name: string;
  preferred: boolean;
  address: string;
  detail: string;
}

interface CompanyRegistListResponse {
  id: number;
  cityProvince: string;
  district: string;
  name: string;
}

// 데이터를 처리할 수 있도록 타입을 union으로 정의
type UserFavorListData = PreferenceCompanyResponse | CompanyRegistListResponse;

interface DataProps {
  datas: UserFavorListData[];
  onRegisterSuccess: () => void; // 콜백 추가
}

export const UserFavorList = ({ datas, onRegisterSuccess }: DataProps) => {
  const [loading, setLoading] = useState(false); // 로딩 상태 추가
  const [error, setError] = useState<string | null>(null); // 에러 상태 추가

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  // 타입 가드 함수로 `PreferenceCompanyResponse` 타입인지 확인
  const isPreferenceCompany = (
    data: UserFavorListData,
  ): data is PreferenceCompanyResponse => {
    return (data as PreferenceCompanyResponse).preferred !== undefined;
  };

  const handleRegister = async (companyId: number) => {
    setLoading(true); // 로딩 상태 시작
    setError(null); // 이전 에러 초기화

    try {
      const response = await fetchPreferenceCompanyRegistReation(companyId);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage('선호 회사 등록에 실패했습니다.');
        setIsModalOpen(true);
      }
      onRegisterSuccess(); // 등록 후 목록을 새로고침하기 위한 콜백 호출
    } catch (error) {
      console.error('선호회사를 등록하는 데 실패했습니다.');
    } finally {
      setLoading(false); // 로딩 상태 종료
    }
  };

  const handleRegistCancel = async (companyId: number) => {
    setLoading(true); // 로딩 상태 시작
    setError(null); // 이전 에러 초기화

    try {
      const response = await fetchPreferenceCompanyCancel(companyId);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
      } else {
        setModalMessage('선호 회사 취소에 실패했습니다.');
        setIsModalOpen(true);
      }
      onRegisterSuccess(); // 등록 후 목록을 새로고침하기 위한 콜백 호출
    } catch (error) {
      console.error('선호회사를 취소하는 데 실패했습니다.');
    } finally {
      setLoading(false); // 로딩 상태 종료
    }
  };

  return (
    <ComponentSection>
      <ul>
        {datas.map((item) => (
          <li key={item.id}>
            <div className="l-box">
              <div className="t-box">
                {item.name}
                {isPreferenceCompany(item) && (
                  <div className="icon-box">
                    {item.preferred ? <StarYellow /> : <StarGray />}
                  </div>
                )}
              </div>
              <div className="b-box">
                {isPreferenceCompany(item)
                  ? item.address
                  : `${item.cityProvince}${
                      item.district ? `, ${item.district}` : ''
                    }`}
              </div>
            </div>
            <div className="r-box">
              <div className="btn-box">
                {isPreferenceCompany(item) ? (
                  item.preferred ? (
                    <button
                      className="cancle"
                      onClick={() => handleRegistCancel(item.id)}
                      disabled={loading}
                    >
                      {loading ? '취소 중...' : '취소'}
                    </button>
                  ) : (
                    <button
                      onClick={() => handleRegister(item.id)}
                      disabled={loading}
                    >
                      {loading ? '등록 중...' : '등록'}
                    </button>
                  )
                ) : (
                  <button
                    className="cancle"
                    onClick={() => handleRegistCancel(item.id)}
                    disabled={loading}
                  >
                    {loading ? '취소 중...' : '취소'}
                  </button>
                )}
              </div>
            </div>
          </li>
        ))}
      </ul>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </ComponentSection>
  );
};

// style

const ComponentSection = styled.section`
  margin-top: 2rem;
  ul {
    li {
      padding: 2.4rem 0.4rem;
      display: flex;
      justify-content: space-between;
      align-items: center;
      .l-box {
        display: flex;
        flex-direction: column;
        gap: 0.8rem;
        .t-box {
          display: flex;
          align-items: center;
          gap: 0.8rem;
          font-size: 1.6rem;
          font-weight: 600;
          .icon-box {
            display: flex;
          }
        }
        .b-box {
          font-size: 1.3rem;
          font-weight: 400;
          color: ${(props) => props.theme.textAAAColor};
        }
      }
      .r-box {
        .btn-box {
          button {
            font-size: 1.3rem;
            font-weight: 400;
            line-height: 2rem;
            height: 3rem;
            width: 4.9rem;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: ${(props) => props.theme.btnBlueColor};
            outline: none;
            border: none;
            border-radius: 0.4rem;
            color: ${(props) => props.theme.textWhiteColor};
            &.cancle {
              color: ${(props) => props.theme.gray900};
              background-color: ${(props) => props.theme.logoutBtnBgColor};
            }
          }
        }
      }
    }
  }
`;
